import { useState } from "react"
import type { FormEvent } from "react"
import { api, ApiError } from "../api/client.ts"
import { useSession } from "../auth/SessionProvider.tsx"
import { useAsyncAction } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import {
  PASSWORD_MIN_LENGTH,
  checkNewPassword,
  isFilled,
  type PasswordProblem,
} from "../lib/password-policy.ts"
import {
  Alert,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Field,
  FormGrid,
  Input,
  PageHeader,
} from "../ui/index.ts"

/**
 * 내 비밀번호 변경.
 *
 * 규칙 판정은 `lib/password-policy.ts`(서버 사본)가 하고, 서버도 받을 때 같은 판정을 다시 한다.
 * 성공하면 서버가 이 계정의 세션을 모두 끊으므로 새 비밀번호로 다시 로그인해야 한다.
 */
export function PasswordPage() {
  const { t } = useI18n()
  const { user, endSession } = useSession()

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("")
  const [done, setDone] = useState(false)
  const change = useAsyncAction()

  const values = { currentPassword, newPassword, newPasswordConfirm }
  // 세 칸이 다 차기 전에는 빨간 글씨를 띄우지 않는다.
  const problem: PasswordProblem | null = isFilled(values) ? checkNewPassword(values) : null
  const canSubmit = isFilled(values) && problem === null && !change.busy

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit) return

    const ok = await change.run(async () => {
      try {
        await api.post("/api/auth/password", values)
      } catch (err) {
        // 화면은 code 로 문구를 찾는다. 알 수 없는 오류도 같은 모양으로 맞춰 준다.
        throw err instanceof ApiError ? err : new ApiError("INTERNAL", 0)
      }
    })
    if (!ok) return

    setCurrentPassword("")
    setNewPassword("")
    setNewPasswordConfirm("")
    setDone(true)
  }

  if (done) {
    return (
      <>
        <PageHeader title={t("password.title")} />
        <Card>
          <Alert tone="ok" title={t("password.doneTitle")}>
            {t("password.doneBody")}
          </Alert>
          <div className="inline">
            <Button variant="primary" icon="logout" onClick={() => endSession()}>
              {t("password.doneLogin")}
            </Button>
          </div>
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader title={t("password.title")} description={t("password.description")} />

      <Card>
        <CardHeader title={t("password.formTitle")} icon="lock" />

        <form onSubmit={(event) => void submit(event)}>
          <ErrorNote error={change.error} />

          {/* 브라우저 비밀번호 관리자가 어떤 계정인지 알아보게 아이디를 숨겨 둔다. */}
          <input type="text" name="username" value={user?.email ?? ""} autoComplete="username" readOnly hidden />

          <FormGrid columns={1}>
            <Field label={t("password.current")} htmlFor="pw-current" required>
              <Input
                id="pw-current"
                type="password"
                value={currentPassword}
                autoComplete="current-password"
                autoFocus
                required
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </Field>

            <Field
              label={t("password.new")}
              htmlFor="pw-new"
              required
              hint={t("password.hint", { min: PASSWORD_MIN_LENGTH })}
              error={
                problem && problem !== "PASSWORD_CONFIRM_MISMATCH" ? t(`error.${problem}`) : undefined
              }
            >
              <Input
                id="pw-new"
                type="password"
                value={newPassword}
                autoComplete="new-password"
                aria-describedby="pw-new-msg"
                required
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </Field>

            <Field
              label={t("password.confirm")}
              htmlFor="pw-confirm"
              required
              error={problem === "PASSWORD_CONFIRM_MISMATCH" ? t(`error.${problem}`) : undefined}
            >
              <Input
                id="pw-confirm"
                type="password"
                value={newPasswordConfirm}
                autoComplete="new-password"
                aria-describedby="pw-confirm-msg"
                required
                onChange={(event) => setNewPasswordConfirm(event.target.value)}
              />
            </Field>
          </FormGrid>

          <div className="inline">
            <Button type="submit" variant="primary" loading={change.busy} disabled={!canSubmit}>
              {change.busy ? t("password.submitting") : t("password.submit")}
            </Button>
          </div>
        </form>
      </Card>
    </>
  )
}
